import React, { Fragment, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { read } from "shapefile";
import JSZip from 'jszip'; // Import JSZip
import mapboxgl from "mapbox-gl";
import { toast } from "react-toastify";
import { useSelector } from 'react-redux';
// import MapboxDraw from "@mapbox/mapbox-gl-draw";
// import 'mapbox-gl/dist/mapbox-gl.css';
import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css";
import FeaturePopup from "./FeaturePopup";
import ShapeFileMap from "./ShapeFileMap";
import * as topojson from 'topojson-client';
import * as toGeoJSON from 'togeojson';
import Wkt from 'wkt';
import { parse as csvParse } from 'csv-parse/browser/esm';
import './FeatureMap.scss'
import './toastPopup.scss'
import { geoJSON } from "leaflet";
import { getEnvVar } from "../../../envUtils";
// @ts-ignore
// eslint-disable-next-line import/no-webpack-loader-syntax
mapboxgl.workerClass = require("worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker").default;

function ShapeFileFeature() {
  const navigate = useNavigate();
  const { mission_name, mission_id } = useParams();
  const [uploadedGeoJSON, setUploadedGeoJSON] = useState(null);
  const [allFeature, setAllFeature] = useState(null)
  const [isDataFetched, setIsDateFetched] = useState(false)
  const [fileName, setFileName] = useState('');
  const mapContainerRef = useRef(null);
  const map = useRef(null);
  const draw = useRef(null);
  const storedFile = useSelector((state) => state.file.selectedFile); // Get file from Redux


  // Initialize the Mapbox map
  useEffect(() => {
    mapboxgl.accessToken = getEnvVar('REACT_APP_MAPBOX_TOKEN') // Mapbox access token for Equirectangular projection
    // On uploading the shape file map is creating again, so commenting this code. This code might require in future.
    // if (mapContainerRef.current) {
    //   map.current = new mapboxgl.Map({
    //     container: mapContainerRef.current,
    //     style: getEnvVar('REACT_APP_MAPBOX_STYLE_URL'), // Mapbox style url for Equirectangular projection
    //     center: [0, 0],
    //     zoom: 2.3,
    //   });
    // }
  }, []);


  useEffect(() => {
    const processShapefile = async () => {
      if (!storedFile || !storedFile.fileName) {
        console.error('No file available or file name missing');
        return;
      }
      try {
        const arrayBuffer = await storedFile.arrayBuffer; // Properly access arrayBuffer
        const zip = await JSZip.loadAsync(arrayBuffer); // Load ZIP file
        const files = Object.values(zip.files);

        const geojsonFiles = []; // Store GeoJSON for each shapefile
        let shpFileFound = false;

        setFileName(storedFile.fileName); // Use storedFile.name

        for (const entry of files) {
          if (entry.name.endsWith('.shp')) {
            shpFileFound = true;
            const shapefileBuffer = await entry.async('arraybuffer');
            const geojson = await read(shapefileBuffer); // Convert shapefile to GeoJSON
            geojsonFiles.push(geojson);
          }
        }

        if (!shpFileFound) {
          navigate(`${process.env.PUBLIC_URL}/${mission_name}/${mission_id}/draw_feature`);
          toast.dismiss();
          toast.error('Invalid Data', { className: 'custom-toast-error align__toast__position', position: "top-right" }, { toastId: '0' });
        } else {
          setUploadedGeoJSON(geojsonFiles);
        }
      } catch (error) {
        // console.error('Error uploading and converting shapefile:', error);
      }
    };

    if (
      storedFile &&
      storedFile.fileName &&
      storedFile.fileName.toLowerCase().endsWith('.zip')
    ) {
      // Access the ArrayBuffer directly
      const buffer = storedFile.arrayBuffer;
      const view = new DataView(buffer);

      // Check the ZIP file signature
      const isZipSignature =
        view.getUint8(0) === 0x50 &&
        view.getUint8(1) === 0x4B &&
        view.getUint8(2) === 0x03 &&
        view.getUint8(3) === 0x04;

      if (isZipSignature) {
        processShapefile(); // Proceed if it's a valid ZIP
      } else {
        navigate(`${process.env.PUBLIC_URL}/${mission_name}/${mission_id}/draw_feature`);
        toast.error('File format not supported', { className: 'custom-toast-error align__toast__position', position: "top-right" }, { toastId: '0' });
      }
    } else {
      console.warn("The file is not a ZIP format.");
    }
  }, [storedFile, mission_name, mission_id, navigate, setUploadedGeoJSON, toast]);

  useEffect(() => {
    if (!storedFile || !storedFile.fileName) {
      console.error('No file available or file name missing');
      return;
    }

    const processFile = async () => {
      try {
        const arrayBuffer = storedFile.arrayBuffer;  // Accessing the arrayBuffer directly (no await or parentheses needed)
        const text = new TextDecoder().decode(arrayBuffer);

        // Set the file name once
        setFileName(storedFile.fileName);

        // Process the file based on its extension
        if (storedFile.fileName.toLowerCase().endsWith('.geojson')) {
          setUploadedGeoJSON(JSON.parse(text));
        } else if (storedFile.fileName.toLowerCase().endsWith('.topojson')) {
          const topoData = JSON.parse(text);
          const geojson = topojson.feature(topoData, topoData.objects[Object.keys(topoData.objects)[0]]);
          setUploadedGeoJSON(geojson);
        } else if (storedFile.fileName.toLowerCase().endsWith('.csv')) {
          csvParse(text, { columns: true }, (err, output) => {
            if (err) {
              console.error('CSV Parsing Error:', err);
              return;
            }

            // Check if the first row contains 'lat' and 'lon' fields
            if (!output[0] || !('lat' in output[0] && 'lon' in output[0])) {
              navigate(`${process.env.PUBLIC_URL}/${mission_name}/${mission_id}/draw_feature`);
              toast.dismiss()
              toast.error('Invalid csv file', { className: 'custom-toast-error align__toast__position', position: "top-right" }, { toastId: '0' });
              return;
            }

            const geojson = {
              type: 'FeatureCollection',
              features: output.map(row => ({
                type: 'Feature',
                properties: row,
                geometry: {
                  type: 'Point',
                  coordinates: [parseFloat(row.lon), parseFloat(row.lat)]  // Adjust based on your CSV structure
                }
              }))
            };
            setUploadedGeoJSON(geojson);
          });
        } else if (storedFile.fileName.toLowerCase().endsWith('.kml')) {
          const parser = new DOMParser();
          const kmlDoc = parser.parseFromString(text, 'application/xml');
          const geojson = toGeoJSON.kml(kmlDoc);
          setUploadedGeoJSON(geojson);
        } else if (storedFile.fileName.toLowerCase().endsWith('.wkt')) {
          const geojson = Wkt.parse(text).toJson();
          setUploadedGeoJSON(geojson);
        } else {
          // console.error('Unsupported file format');
        }
      } catch (error) {
        // console.error('Error processing file:', error);
      }
    };

    processFile(); // Call the function to process the file

  }, [storedFile, setUploadedGeoJSON]);

  useEffect(() => {
    let featureList = [];
    if (Array.isArray(uploadedGeoJSON)) {
      uploadedGeoJSON.forEach((item) => {
        item?.features?.forEach((featureItem) => {
          // Handling MultiPolygon and Polygon types
          if (featureItem?.geometry?.type === 'MultiPolygon' || featureItem?.geometry?.type === 'Polygon') {
            featureItem?.geometry?.coordinates.forEach((coordinateSet) => {
              if (Array.isArray(coordinateSet) && coordinateSet.every(coord => Array.isArray(coord) && coord.length === 2)) {
                sessionStorage.setItem("flatArrayFlag", "true");
              }
              let feature = {
                type: featureItem?.geometry?.type,
                coordinates: coordinateSet,
                name: '',
                description: ''
              };
              featureList.push(feature);
            });
          }
          // Handling Point type
          else if (featureItem?.geometry?.type === 'Point') {
            let feature = {
              type: featureItem?.geometry?.type,
              coordinates: [featureItem?.geometry?.coordinates?.[0], featureItem?.geometry?.coordinates?.[1]],
              name: '',
              description: ''
            };
            featureList.push(feature);
          }
        });
      });
    } else {
      // Handle the simpler GeoJSON structure (like your second example with just a Polygon)
      uploadedGeoJSON?.features?.forEach((featureItem) => {
        // Handling Polygon or MultiPolygon types
        if (featureItem?.geometry?.type === 'MultiPolygon' || featureItem?.geometry?.type === 'Polygon') {
          featureItem?.geometry?.coordinates.forEach((coordinateSet) => {
            if (Array.isArray(coordinateSet) && coordinateSet.every(coord => Array.isArray(coord) && coord.length === 2)) {
              sessionStorage.setItem("flatArrayFlag", "true");
            }
            let feature = {
              type: featureItem?.geometry?.type,
              coordinates: coordinateSet,
              name: '',
              description: ''
            };
            featureList.push(feature);
          });
        }
        // Handling Point type
        else if (featureItem?.geometry?.type === 'Point') {
          let feature = {
            type: featureItem?.geometry?.type,
            coordinates: [featureItem?.geometry?.coordinates?.[0], featureItem?.geometry?.coordinates?.[1]],
            name: '',
            description: ''
          };
          featureList.push(feature);
        }
      });
    }
    // http://localhost:3000/test/7e381569-f616-479d-9096-1047df289882/draw_feature
    // Set all features and trigger the fetched state
    setAllFeature(featureList);
    if (featureList?.length > 0) {
      setIsDateFetched(true);
    } else if (uploadedGeoJSON && featureList?.length === 0) {
      navigate(`${process.env.PUBLIC_URL}/${mission_name}/${mission_id}/draw_feature`);
      toast.dismiss()
      toast.error('File is empty', { className: 'custom-toast-error align__toast__position', position: "top-right" }, { toastId: '0' });
    }
  }, [uploadedGeoJSON]);
  return (
    <Fragment>
      <div>
        <div className="w-100 h-100 FeatureMap position-absolute" ref={mapContainerRef} />
        {(uploadedGeoJSON !== null || uploadedGeoJSON?.length === 0) &&
          <div className="listOfFeatures">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="16" viewBox="0 0 14 16" fill="none">
              <path d="M13.367 3.27517L10.3437 0.137917C10.2588 0.0496395 10.1435 -1.22337e-05 10.0233 -0.00012207H3.97674C3.69611 -0.00012207 3.42697 0.115564 3.22853 0.321486C3.03009 0.527409 2.9186 0.806699 2.9186 1.09792V2.50968H1.55814C1.2775 2.50968 1.00836 2.62537 0.809922 2.83129C0.611482 3.03721 0.5 3.3165 0.5 3.60772V14.9018C0.5 15.1931 0.611482 15.4723 0.809922 15.6783C1.00836 15.8842 1.2775 15.9999 1.55814 15.9999H10.0233C10.3039 15.9999 10.573 15.8842 10.7715 15.6783C10.9699 15.4723 11.0814 15.1931 11.0814 14.9018V13.4901H12.4419C12.7225 13.4901 12.9916 13.3744 13.1901 13.1685C13.3885 12.9625 13.5 12.6833 13.5 12.392V3.60772C13.4999 3.48296 13.452 3.36334 13.367 3.27517ZM10.1744 14.9018C10.1744 14.9434 10.1585 14.9833 10.1301 15.0128C10.1018 15.0422 10.0633 15.0587 10.0233 15.0587H1.55814C1.51805 15.0587 1.4796 15.0422 1.45125 15.0128C1.4229 14.9833 1.40698 14.9434 1.40698 14.9018V3.60772C1.40698 3.56612 1.4229 3.52622 1.45125 3.4968C1.4796 3.46738 1.51805 3.45086 1.55814 3.45086H7.41645L10.1744 6.31282V14.9018ZM12.593 12.392C12.593 12.4336 12.5771 12.4735 12.5487 12.503C12.5204 12.5324 12.482 12.5489 12.4419 12.5489H11.0814V6.11753C11.0813 5.99276 11.0334 5.87314 10.9484 5.78498L7.92512 2.64772C7.84015 2.55944 7.72488 2.50979 7.60465 2.50968H3.82558V1.09792C3.82558 1.05631 3.84151 1.01642 3.86986 0.986998C3.8982 0.957581 3.93665 0.941054 3.97674 0.941054H9.83506L12.593 3.80302V12.392ZM8.05814 9.88223C8.05814 10.007 8.01036 10.1267 7.92532 10.215C7.84027 10.3032 7.72492 10.3528 7.60465 10.3528H3.97674C3.85647 10.3528 3.74113 10.3032 3.65608 10.215C3.57103 10.1267 3.52326 10.007 3.52326 9.88223C3.52326 9.75742 3.57103 9.63773 3.65608 9.54947C3.74113 9.46122 3.85647 9.41164 3.97674 9.41164H7.60465C7.72492 9.41164 7.84027 9.46122 7.92532 9.54947C8.01036 9.63773 8.05814 9.75742 8.05814 9.88223ZM8.05814 12.392C8.05814 12.5168 8.01036 12.6365 7.92532 12.7248C7.84027 12.813 7.72492 12.8626 7.60465 12.8626H3.97674C3.85647 12.8626 3.74113 12.813 3.65608 12.7248C3.57103 12.6365 3.52326 12.5168 3.52326 12.392C3.52326 12.2672 3.57103 12.1475 3.65608 12.0593C3.74113 11.971 3.85647 11.9214 3.97674 11.9214H7.60465C7.72492 11.9214 7.84027 11.971 7.92532 12.0593C8.01036 12.1475 8.05814 12.2672 8.05814 12.392Z" fill="#F0F0F0CC" />
            </svg>
            <span className="ms-1"> {fileName} </span>
          </div>
        }
      </div>
      <div>
        {isDataFetched && <ShapeFileMap setAllFeature={setAllFeature} allFeature={allFeature} fileName={fileName} />}
      </div>
      {
        !isDataFetched &&
        <button className="closeButton" onClick={() => {
          navigate(`${process.env.PUBLIC_URL}/${mission_name}/_details/${mission_id}/Features`)
          if (uploadedGeoJSON !== null && uploadedGeoJSON.length === 0) {
            toast.dismiss();
            toast.error('File is empty', {
              className: 'custom-toast-error align_toast_position_1', // Add your custom class here
              position: 'top-right'
            }, { toastId: '0' }
            );
          }
        }}>
          &times;
        </button>
      }
    </Fragment >
  )
}

export default ShapeFileFeature;